import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Box,
  Paper,
  Button,
  Modal,
  Card,
  CardContent,
  useTheme,
  useMediaQuery, 
  Stack,         
  Divider        
} from "@mui/material";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { styled } from "@mui/system";

import { useAuth } from "../../hooks/useAuth";
import { getCurrentOrder } from "../../hooks/apiHandler";

// --- STYLED COMPONENTS ---
const FlashingDot = styled("span")(({ theme, isopen }) => ({
  display: "inline-block",
  width: 10,
  height: 10,
  borderRadius: "50%",
  backgroundColor: isopen ? theme.palette.success.main : theme.palette.warning.main,
  marginRight: 8,
  animation: "blinking 1.5s infinite",
  "@keyframes blinking": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.4 },
    "100%": { opacity: 1 },
  },
}));

// --- HELPER FUNCTIONS ---
const getOrderTypeChip = (typeCode) => {
  switch (typeCode) {
    case 0: return { label: "In-Store", color: "info" };
    case 1: return { label: "Pickup", color: "primary" };
    case 2: return { label: "Kiosk", color: "secondary" };
    case 3: return { label: "Online", color: "warning" };
    default: return { label: "Unknown", color: "default" };
  }
};

const getOrderStatusChip = (statusCode) => {
  switch (statusCode) {
    case 1: return { label: "Active", color: "primary" };
    case 2: return { label: "Completed", color: "success" };
    case 5: return { label: "Cancelled", color: "error" };
    case 0: 
    default: return { label: "In Progress", color: "info" };
  }
};

const formatDateTime = (isoDateString) => {
  if (!isoDateString) return null;
  const date = new Date(isoDateString);
  return `${date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
};

// --- SUB-COMPONENTS ---
const StatCard = ({ title, value, color }) => {
  const theme = useTheme();
  return (
    <Card elevation={3} sx={{ height: '100%', borderLeft: `5px solid ${theme.palette[color].main}` }}>
      <CardContent>
        <Typography variant="h6" color={theme.palette[color].dark} gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

// --- MAIN COMPONENT ---
export const Order = () => {
  const theme = useTheme();
  // Detect if screen is smaller than 'md' (tablet/mobile)
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const { Logout } = useAuth();
  
  // Safe check for localStorage to prevent crashes if key is missing
  const getStoredID = () => {
    try {
        return JSON.parse(window.localStorage.getItem("ds"));
    } catch (e) {
        return null;
    }
  };
  const storeID = useRef(getStoredID());

  const [orderData, setOrderData] = useState([]);
  const [displayOrder, setDisplayOrder] = useState([]);
  const [reloadFlag, setReloadFlag] = useState(false);
  const [listOrderType, setListOrderType] = useState("all");
  const [displayTime, setDisplayTime] = useState(null);
  const [cdata, setCData] = useState({
    currentOrder: 0,
    inStore: 0,
    online: 0,
    pickup_kiosk: 0,
    partners: 0,
    completed: 0,
    storeOpen: false, 
  });

  const [open, setOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const handleOpen = (order) => {
    setSelectedOrder(order);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedOrder(null);
  };

  useEffect(() => {
    async function fetchOrders() {
      if (!storeID.current) return; // Prevent api call if no store ID

      try {
        const response = await getCurrentOrder({ storeID: storeID.current }, Logout);
        const data = response?.data; 

        if (data && data.orders) {
          const allOrders = data.orders;
          const displayTimeStr = data.dailyData.openingTime
            ? formatDateTime(data.dailyData.openingTime)
            : null;

          setDisplayTime(displayTimeStr ? `Store opened: ${displayTimeStr}` : 'No Data.');
          
          setCData({
            currentOrder: allOrders.filter(o => o.oStatus === 1).length,
            inStore: allOrders.filter(o => o.type === 0).length,
            pickup_kiosk: allOrders.filter(o => o.type === 1 || o.type === 2).length,
            online: allOrders.filter(o => o.type === 3).length,
            partners: allOrders.filter(o => o.type === 3 || o.type === 5).length,
            completed: allOrders.filter(o => o.oStatus === 2).length,
            storeOpen: data.dailyData.dayStatus,
          });
          
          setOrderData(allOrders);
          
          // Re-apply filter if data refreshes
          if(listOrderType === 'all') setDisplayOrder(allOrders);
          else if(listOrderType === 'cmpt') setDisplayOrder(allOrders.filter(o => o.oStatus === 2));
          else if(listOrderType === 'crnt') setDisplayOrder(allOrders.filter(o => o.oStatus === 1));
          else if(listOrderType === 'canc') setDisplayOrder(allOrders.filter(o => o.oStatus === 5));

        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
      setReloadFlag(false);
    }

    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadFlag, Logout]); // Added dependencies to silence warnings

  const handleOrderDisplay = (e) => {
    if (!orderData) return;
    const selectedType = e.target.value;
    setListOrderType(selectedType);

    switch (selectedType) {
      case "all": setDisplayOrder(orderData); break;
      case "cmpt": setDisplayOrder(orderData.filter(o => o.oStatus === 2)); break;
      case "crnt": setDisplayOrder(orderData.filter(o => o.oStatus === 1)); break;
      case "canc": setDisplayOrder(orderData.filter(o => o.oStatus === 5)); break;
      default: setDisplayOrder([]);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: isMobile ? 1 : 3 }}>
      
      {/* 📌 HEADER & STORE STATUS */}
      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3, background: theme.palette.grey[100] }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary">
              Daily Order Dashboard
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ textAlign: { xs: 'left', sm: 'right' }, mt: { xs: 1, sm: 0 } }}>
            <Typography variant="body2" color="textSecondary" display="block" gutterBottom>
               {displayTime}
            </Typography>
            <Box display="inline-flex" alignItems="center">
                <Typography variant="body1" fontWeight="500" sx={{ mr: 1 }}>
                  Status:
                </Typography>
                <Chip
                  size="small"
                  label={
                    <Box display="flex" alignItems="center">
                      <FlashingDot isopen={cdata.storeOpen ? 1 : 0} />
                      <Typography variant="caption" fontWeight="bold">
                        {cdata.storeOpen ? "OPEN" : "CLOSED"}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    borderColor: cdata.storeOpen ? theme.palette.success.main : theme.palette.warning.main,
                    color: cdata.storeOpen ? theme.palette.success.main : theme.palette.warning.main,
                  }}
                  variant="outlined"
                />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 📌 ORDER SUMMARY CARDS */}
      {/* XS={6} makes them side-by-side on mobile, much better than stacked */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard title="Current" value={cdata.currentOrder} color="primary" />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard title="Completed" value={cdata.completed} color="success" />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard title="In-Store" value={cdata.inStore} color="info" />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard title="Online" value={cdata.online + cdata.partners} color="secondary" />
        </Grid>
      </Grid>

      {/* 📌 MAIN CONTENT AREA */}
      <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, borderRadius: 2 }}>
        {/* Filter Controls */}
        <Grid container spacing={2} justifyContent="flex-end" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter Orders</InputLabel>
              <Select value={listOrderType} onChange={handleOrderDisplay} label="Filter Orders">
                <MenuItem value="all">All Orders</MenuItem>
                <MenuItem value="crnt">Active/Current</MenuItem>
                <MenuItem value="cmpt">Completed</MenuItem>
                <MenuItem value="canc">Canceled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* 📌 CONDITIONAL RENDERING: MOBILE (CARDS) vs DESKTOP (TABLE) */}
        {isMobile ? (
             /* --- MOBILE VIEW --- */
             <Stack spacing={2}>
                {displayOrder.length > 0 ? (
                    displayOrder.map((order, index) => {
                        const orderType = getOrderTypeChip(order.type);
                        const orderStatus = getOrderStatusChip(order.oStatus);
                        return (
                            <Card key={index} variant="outlined" sx={{ borderLeft: `5px solid ${theme.palette[orderStatus.color].main}` }}>
                                <CardContent sx={{ pb: 1, '&:last-child': { pb: 2 } }}>
                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Typography variant="h6" fontWeight="bold">#{order.orderNo}</Typography>
                                        <Typography variant="h6" color="primary">${(order.total || 0).toFixed(2)}</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="textSecondary">Status</Typography>
                                            <Box><Chip label={orderStatus.label} color={orderStatus.color} size="small" /></Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="textSecondary">Type</Typography>
                                            <Box><Chip label={orderType.label} color={orderType.color} size="small" variant="outlined" /></Box>
                                        </Grid>
                                        <Grid item xs={12} sx={{ mt: 1 }}>
                                            <Typography variant="caption" color="textSecondary">Time: {new Date(order.startTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</Typography>
                                        </Grid>
                                    </Grid>
                                    <Button fullWidth variant="contained" size="small" sx={{ mt: 2 }} onClick={() => handleOpen(order)}>
                                        View Details
                                    </Button>
                                </CardContent>
                            </Card>
                        )
                    })
                ) : (
                    <Typography align="center" sx={{ py: 4 }} color="textSecondary">No orders found.</Typography>
                )}
             </Stack>
        ) : (
             /* --- DESKTOP VIEW --- */
            <TableContainer component={Box} sx={{ maxHeight: 600, overflowX: "auto" }}>
              <Table stickyHeader sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey[200] }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Order No</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Payment</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date/Time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayOrder.length > 0 ? (
                    displayOrder.map((order, index) => {
                      const orderType = getOrderTypeChip(order.type);
                      const orderStatus = getOrderStatusChip(order.oStatus);
                      const paymentSuccessful = order.payment && order.payment.length > 0;
                      return (
                        <TableRow key={index} hover sx={{ '&:hover': { backgroundColor: theme.palette.grey[50] } }}>
                          <TableCell>{order.orderNo}</TableCell>
                          <TableCell><Chip label={orderType.label} color={orderType.color} size="small" variant="outlined" /></TableCell>
                          <TableCell><Chip label={orderStatus.label} color={orderStatus.color} size="small" /></TableCell>
                          <TableCell align="right">${(order.total || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip label={paymentSuccessful ? "Paid" : "Pending"} color={paymentSuccessful ? "success" : "default"} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>{new Date(order.startTime).toLocaleString()}</TableCell>
                          <TableCell>
                            <Button onClick={() => handleOpen(order)} variant="outlined" size="small">View</Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body1" color="textSecondary" sx={{ py: 2 }}>
                          No Orders to Display.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        )}
      </Paper>

      {/* 📌 MODAL */}
      <Modal open={open} onClose={handleClose} aria-labelledby="order-details-modal">
        <Box
          sx={{
            position: "absolute",
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '600px' }, // Responsive width
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: "background.paper",
            boxShadow: 24,
            p: { xs: 2, md: 4 }, // Responsive padding
            borderRadius: 2
          }}
        >
          {selectedOrder && (
            <>
               <Typography id="order-details-modal" variant="h5" sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 1 }}>
                Order #{selectedOrder.orderNo}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Status</Typography>
                  <Box><Chip label={getOrderStatusChip(selectedOrder.oStatus).label} color={getOrderStatusChip(selectedOrder.oStatus).color} size="small" /></Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Total</Typography>
                  <Typography variant="h6" color="success.main">${(selectedOrder.total || 0).toFixed(2)}</Typography>
                </Grid>
                
                {selectedOrder.custInfo && (
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 1, mt: 1, bgcolor: theme.palette.grey[50] }}>
                            <Typography variant="subtitle2">Customer Info</Typography>
                            <Typography variant="body2">Name: {selectedOrder.custInfo.name || 'N/A'}</Typography>
                            <Typography variant="body2">Phone: {selectedOrder.custInfo.phone || 'N/A'}</Typography>
                        </Paper>
                    </Grid>
                )}
              </Grid>

              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Items</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: theme.palette.grey[100] }}>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.orders?.map((group) => 
                        group.order?.map((item, i) => (
                            <TableRow key={i}>
                                <TableCell>
                                    {item.item}
                                    {item.comment && <Typography variant="caption" display="block" color="error">Note: {item.comment}</Typography>}
                                </TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">${(item.price * item.quantity).toFixed(2)}</TableCell>
                            </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Button variant="contained" fullWidth onClick={handleClose}>Close</Button>
            </>
          )}
        </Box>
      </Modal>
    </Container>
  );
};